import {
  applyFieldFormat,
  feeInputSafe,
  FIELD_FORMAT_DEFAULTS,
  formatAmountOnBlur,
  numOnly,
  pctInputSafe,
  type FieldFormat,
} from "@/lib/formatters";
import { FormField, formFieldDescribedBy, type FormFieldWidth } from "./FormField";
import type { FieldHelpDisplay } from "./FieldHelp";

/** Plain text input inside the standard FormField frame. */
export function TF({
  id,
  label,
  value,
  onChange,
  type = "text",
  placeholder,
  maxLength,
  required,
  hint,
  helpDisplay,
  sensitive,
  error,
  inputMode,
  autoComplete = "off",
  numeric,
  format,
  width,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  maxLength?: number;
  required?: boolean;
  hint?: string;
  /** Where the hint renders. Defaults to a tooltip (operator forms). */
  helpDisplay?: FieldHelpDisplay;
  sensitive?: boolean;
  error?: string;
  inputMode?: "text" | "email" | "tel" | "numeric" | "decimal";
  autoComplete?: string;
  pattern?: string;
  // When set, keystrokes are filtered before reaching onChange so a letter
  // can't be typed into the field -- "currency" keeps digits/"."/"$",
  // "percent" keeps digits/"."/"%", "integer" keeps digits only (see
  // feeInputSafe / pctInputSafe / numOnly). Used by the admin fee-, rate-,
  // and whole-number override fields. "currency"/"percent" also reformat on
  // blur to "$5.00" / "5.00%" (formatAmountOnBlur).
  numeric?: "currency" | "percent" | "integer";
  /** Standard mask/filter (phone, ZIP, names, …). See FieldFormat. */
  format?: FieldFormat;
  width?: FormFieldWidth;
}) {
  const defaults = format ? FIELD_FORMAT_DEFAULTS[format] : {};
  const resolvedInputMode = inputMode ?? defaults.inputMode;
  const resolvedMaxLength = maxLength ?? defaults.maxLength;
  const resolvedPlaceholder = placeholder ?? defaults.placeholder;
  return (
    <FormField
      id={id}
      label={label}
      required={required}
      hint={hint}
      helpDisplay={helpDisplay}
      sensitive={sensitive}
      error={error}
      width={width}
    >
      <input
        id={id}
        type={type}
        inputMode={resolvedInputMode}
        value={value}
        onChange={(e) => {
          const raw = e.target.value;
          if (format) {
            onChange(applyFieldFormat(format, raw));
            return;
          }
          onChange(
            numeric === "percent"
              ? pctInputSafe(raw)
              : numeric === "currency"
                ? feeInputSafe(raw)
                : numeric === "integer"
                  ? numOnly(raw)
                  : raw,
          );
        }}
        onBlur={
          numeric === "currency" || numeric === "percent"
            ? () => onChange(formatAmountOnBlur(value, numeric))
            : undefined
        }
        className="input"
        placeholder={resolvedPlaceholder}
        maxLength={resolvedMaxLength}
        aria-required={required || undefined}
        autoComplete={autoComplete}
        data-1p-ignore="true"
        data-lpignore="true"
        aria-invalid={!!error || undefined}
        aria-describedby={formFieldDescribedBy(id, { error, hint, helpDisplay })}
      />
    </FormField>
  );
}
